import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { objectiveActionsSchema, ObjectiveActionsValidationSchema } from "../../schemas/objectiveActions.schema";
import useCreateObjectiveAction from "../../hooks/useCreateMaintenanceObjectiveAction";
import TextArea from "@/common/components/TextArea";

const MaintenanceObjectiveActionsCreate = () => {
  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);


  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    mutate: createObjectiveActions,
    isPending: isCreateObjectiveActionsPending,
  } = useCreateObjectiveAction({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveActions'] });
      goBack({ fallback: `/objective-maintenance/actions` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ObjectiveActionsValidationSchema>({
    resolver: zodResolver(objectiveActionsSchema),
  });

  const onSubmit: SubmitHandler<ObjectiveActionsValidationSchema> = (data) => createObjectiveActions(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={
        hasUserPermission('Admin::Maintenance::ObjectiveActions::Create') || isUserPermissionsPending
          ? 200
          : 403
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/objective-maintenance/actions` }}
          titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-base"
          title="Objetos de manutenção"
          subtitle="Nova Ação"
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/objective-maintenance/actions` })}
                disabled={isCreateObjectiveActionsPending}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                name="submit"
                isLoading={isCreateObjectiveActionsPending}
              >
                Criar Ação
              </Button>
            </div>
          )}
        />

        <Box className="max-h-[calc(100vh-132px)] overflow-y-auto flex flex-col gap-4">
          <p className="text-lg font-medium">
            Dados gerais da ação
          </p>

          <TextField
            label="Nome da ação"
            maxLength={32}
            {...register('display_name')}
            disabled={isCreateObjectiveActionsPending}
            errorMessage={errors.display_name?.message || undefined}
            markAsRequired
          />

          <TextArea
            label="Descrição"
            {...register('description')}
            disabled={isCreateObjectiveActionsPending}
            errorMessage={errors.description?.message || undefined}
            markAsRequired
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectiveActionsCreate;
